import { useState, useEffect, useCallback } from "react";

interface ExitIntentPopupProps {
  onCtaClick: () => void;
}

const STORAGE_KEY = "cgs_exit_popup_seen";

export default function ExitIntentPopup({ onCtaClick }: ExitIntentPopupProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  const show = useCallback(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(true);
    setTimeout(() => setAnimating(true), 10);
  }, []);

  const dismiss = useCallback(() => {
    setAnimating(false);
    setTimeout(() => setVisible(false), 350);
  }, []);

  const handleCta = useCallback(() => {
    dismiss();
    setTimeout(() => onCtaClick(), 360);
  }, [dismiss, onCtaClick]);

  // Exit-intent: mouse leaves toward the top
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 8) show();
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [show]);

  // Mobile fallback: show after 25s of inactivity or scroll-back to top
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const scheduleTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(show, 25000);
    };
    scheduleTimer();
    window.addEventListener("touchstart", scheduleTimer, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("touchstart", scheduleTimer);
    };
  }, [show]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(4px)",
        opacity: animating ? 1 : 0,
        transition: "opacity 0.35s ease",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: "#0A0A0A",
          border: "1px solid rgba(201, 149, 111, 0.35)",
          transform: animating ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
          transition: "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {/* Gold accent bar */}
        <div
          className="w-full h-1"
          style={{ background: "linear-gradient(90deg, #C9956F 0%, #B07048 100%)" }}
        />

        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-200"
          style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.5)" }}
          aria-label="Close"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="px-8 pb-8 pt-6">
          {/* Icon */}
          <div
            className="w-14 h-14 flex items-center justify-center rounded-full mb-5"
            style={{ background: "rgba(201, 149, 111, 0.12)", border: "1px solid rgba(201, 149, 111, 0.35)" }}
          >
            <i className="ri-search-eye-line text-2xl" style={{ color: "#C9956F" }} />
          </div>

          {/* Badge */}
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{
              background: "rgba(201, 149, 111, 0.12)",
              color: "#C9956F",
              border: "1px solid rgba(201, 149, 111, 0.3)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Before You Go
          </span>

          {/* Headline */}
          <h2
            className="text-2xl font-bold mb-3 leading-snug"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "#FFFFFF",
              letterSpacing: "-0.01em",
            }}
          >
            Your Med Spa Could Be Missing<br />
            <span style={{ color: "#C9956F" }}>20–40 New Clients</span> Every Month
          </h2>

          {/* Body */}
          <p
            className="text-sm leading-relaxed mb-6"
            style={{ color: "rgba(255,255,255,0.55)", fontFamily: "'Inter', sans-serif" }}
          >
            Most med spas have 3–5 silent gaps in their marketing that quietly bleed leads. A free 15-minute audit will reveal exactly where yours are — and how to close them fast.
          </p>

          {/* Social proof micro-row */}
          <div
            className="flex items-center gap-3 mb-6 px-4 py-3 rounded-lg"
            style={{ background: "rgba(201, 149, 111, 0.07)", border: "1px solid rgba(201, 149, 111, 0.18)" }}
          >
            <i className="ri-shield-check-line text-lg flex-shrink-0" style={{ color: "#C9956F" }} />
            <p
              className="text-xs"
              style={{ color: "rgba(255,255,255,0.6)", fontFamily: "'Inter', sans-serif" }}
            >
              100% free, zero obligation. Just 15 minutes and a clear action plan you can use right away.
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={handleCta}
            className="whitespace-nowrap w-full flex items-center justify-center gap-2.5 py-4 rounded-xl font-semibold text-white text-sm cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-95"
            style={{
              background: "linear-gradient(135deg, #C9956F 0%, #B07048 100%)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            <i className="ri-calendar-check-line text-base" />
            Yes — Claim My Free Audit
            <i className="ri-arrow-right-line text-base" />
          </button>

          {/* Dismiss */}
          <button
            onClick={dismiss}
            className="whitespace-nowrap w-full text-center mt-3 text-xs py-2 cursor-pointer transition-colors duration-200"
            style={{ color: "rgba(255,255,255,0.3)", fontFamily: "'Inter', sans-serif" }}
          >
            No thanks, I don&apos;t want more clients
          </button>
        </div>
      </div>
    </div>
  );
}
